import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import type { WorkerView } from '@/types/ui/resource-availability'
import { useLanguage } from '@/contexts/language-context'

interface WorkerBreakdownChartProps {
  worker: WorkerView
}

/**
 * Tooltip that hides zero-valued series — keeps the popover focused on what's actually in
 * the bar. Entries are reversed from Recharts' default (JSX/stack-bottom order) so the
 * tooltip reads top-to-bottom in the same order as the visible stack: Stat → PTO →
 * Available → Allocated.
 */
/**
 * Single horizontal line painted along the bottom of the plot area, in the X-axis stroke
 * colour. Customized renders inside the chart's SVG and sits after the Bar layer in DOM
 * order, so the line covers each bar's coloured base + white stroke and presents the X axis
 * as one continuous edge.
 */
function PlotBaseline(props: { offset?: { left: number; top: number; width: number; height: number } }) {
  const offset = props.offset
  if (!offset) return null
  const y = offset.top + offset.height
  return (
    <line
      x1={offset.left}
      x2={offset.left + offset.width}
      y1={y}
      y2={y}
      stroke="rgba(0,0,0,0.4)"
      strokeWidth={1}
    />
  )
}

/**
 * Year tier rendered above the bars — mirrors the heatmap and team waterfall's year row so
 * the drill-down chart reads consistently. Each year label is centred over its column
 * span; year boundaries (everything past the first year) get a vertical tick mark down
 * into the plot area for a continuous visual divider.
 */
function YearTier(props: {
  offset?: { left: number; top: number; width: number; height: number }
  groups: { year: number; firstIdx: number; lastIdx: number }[]
  totalCols: number
}) {
  const { offset, groups, totalCols } = props
  if (!offset || totalCols === 0 || groups.length === 0) return null
  const colWidth = offset.width / totalCols
  // Year tier sits below the x-axis tick labels — a horizontal bracket with short upward
  // ticks at each end frames the months above it, and the year label rides below the
  // bracket bar. Hosting these inside the expanded XAxis height keeps Recharts' layout
  // math intact (plot/legend/etc. all flow around the axis area as usual).
  const lineColor = '#555'
  const lineWidth = 1.5
  const bracketY = offset.top + offset.height + 30
  const bracketTickHeight = 5
  const yearLabelY = bracketY + 14
  const tickTop = offset.top
  const tickBottom = offset.top + offset.height
  return (
    <g>
      {groups.map(g => {
        const startX = offset.left + g.firstIdx * colWidth
        const endX = offset.left + (g.lastIdx + 1) * colWidth
        const centerX = (startX + endX) / 2
        return (
          <g key={`y-${g.year}`}>
            {/* Horizontal bar with upward feet — frames the months above. */}
            <line
              x1={startX}
              x2={endX}
              y1={bracketY}
              y2={bracketY}
              stroke={lineColor}
              strokeWidth={lineWidth}
            />
            <line
              x1={startX}
              x2={startX}
              y1={bracketY}
              y2={bracketY - bracketTickHeight}
              stroke={lineColor}
              strokeWidth={lineWidth}
            />
            <line
              x1={endX}
              x2={endX}
              y1={bracketY}
              y2={bracketY - bracketTickHeight}
              stroke={lineColor}
              strokeWidth={lineWidth}
            />
            <text
              x={centerX}
              y={yearLabelY}
              textAnchor="middle"
              fontSize={11}
              fontWeight={600}
              fill="rgba(0,0,0,0.7)"
            >
              {g.year}
            </text>
          </g>
        )
      })}
      {groups.slice(1).map(g => {
        const x = offset.left + g.firstIdx * colWidth
        return (
          <line
            key={`yd-${g.year}`}
            x1={x}
            x2={x}
            y1={tickTop}
            y2={tickBottom}
            stroke={lineColor}
            strokeWidth={lineWidth}
          />
        )
      })}
    </g>
  )
}

function BreakdownTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null
  const entries = [...payload]
    .reverse()
    .filter(p => typeof p.value === 'number' && p.value > 0)
  if (entries.length === 0) return null
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-md">
      <div className="font-medium mb-1">{label}</div>
      {entries.map(e => (
        <div key={String(e.dataKey)} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: e.color }} />
          <span className="text-muted-foreground">{e.name}:</span>
          <span className="tabular-nums">{e.value}h</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Forward-looking allocation breakdown for one worker. Stacks per month:
 *   Allocated  — working capacity × alloc%
 *   PTO        — known/approved
 *   Stat       — company holidays
 *   Available  — whatever working capacity isn't allocated (folds in fully-available months)
 *
 * Trailing utilization (utilized/non-utilized hour breakdown) is intentionally not shown
 * here — that view lives on the engagement portfolio dashboard's Utilization widget.
 */
export function WorkerBreakdownChart({ worker }: WorkerBreakdownChartProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.breakdown
  const data = useMemo(() => {
    return worker.months.map(c => {
      // Round each piece independently so stack heights stay legible. Allocated hours use
      // plannedWorkingHours (budget on genuinely-worked days) — NOT plannedHours, which the
      // backend counts on PTO/stat days too. Using it here keeps allocated + PTO + stat +
      // available summing to capacity instead of over-filling it, and keeps Available
      // consistent with availablePercent.
      const capacityRounded = Math.round(c.capacityHours)
      const ptoRounded = Math.round(c.ptoHours)
      const statRounded = Math.round(c.statHours)
      const allocatedHours = Math.round(c.plannedWorkingHours)
      const consumed = allocatedHours + ptoRounded + statRounded
      const available = Math.max(0, capacityRounded - consumed)
      // The year-boundary signal is carried by the bracket/label tier under the axis, not
      // on the month tick label, so the per-month string stays plain ("Jan", "Feb", …).
      // Recharts uses `month` as both the tick label and the category key; if the same
      // window ever shows the same calendar month across years, we'll switch to a hidden
      // unique key + tickFormatter rather than mutate the visible label.
      return {
        month: c.monthLabel,
        year: new Date(c.monthStart + 'T00:00:00Z').getUTCFullYear(),
        allocated: allocatedHours,
        pto: ptoRounded,
        stat: statRounded,
        available,
      }
    })
  }, [worker])

  // Year groups for the year tier rendered below the x-axis. One label per calendar year,
  // centred under the year's column range and framed by a bracket — same year-context
  // pattern the heatmap uses so the drill-down reads consistently with the team page.
  interface YearGroup { year: number; firstIdx: number; lastIdx: number }
  const yearGroups = useMemo(() => {
    const groups: YearGroup[] = []
    data.forEach((d, i) => {
      const last = groups[groups.length - 1]
      if (last && last.year === d.year) last.lastIdx = i
      else groups.push({ year: d.year, firstIdx: i, lastIdx: i })
    })
    return groups
  }, [data])

  return (
    <div className="rounded-md border bg-card p-4">
      <ResponsiveContainer width="100%" height={300}>
        {/* X-axis height is bumped to host the year-tier band rendered below the month tick
            labels (Customized). Top margin keeps a small breathing space above the bars. */}
        <BarChart data={data} margin={{ top: 12, right: 16, bottom: 8, left: 8 }}>
          <CartesianGrid stroke="rgba(0,0,0,0.05)" vertical={false} />
          <XAxis dataKey="month" stroke="rgba(0,0,0,0.4)" fontSize={11} height={56} />
          <YAxis stroke="rgba(0,0,0,0.4)" fontSize={11} tickFormatter={v => `${v}h`} />
          <Tooltip content={<BreakdownTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar stackId="hours" dataKey="allocated" name={t?.allocated || 'Allocated'} fill="#3B82F6" />
          <Bar stackId="hours" dataKey="available" name={t?.available || 'Available'} fill="#D1D5DB" />
          <Bar stackId="hours" dataKey="pto" name={t?.pto || 'PTO'} fill="#FB923C" />
          <Bar stackId="hours" dataKey="stat" name={t?.statHoliday || 'Stat holiday'} fill="#10B981" />
          <Customized component={PlotBaseline} />
          <Customized component={(props: object) => (
            <YearTier {...(props as { offset?: { left: number; top: number; width: number; height: number } })} groups={yearGroups} totalCols={data.length} />
          )} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
